import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StoredTileDefinition } from './hero-tile.model';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { CalcEngineRequest, CalcEngineResponse } from 'src/app/services/models/calc-engine.model';
import { UserBoardComponentMode } from 'src/app/services/models/user-board.model';
import { MetricService } from 'src/app/services/metric.service';
import { SelectedFilterUpdatesService } from 'src/app/common/bhub-common-module/services/selected-filter-updates.service';
import { HeroPiece, ITileComponent, StoredHeroDataTypes, StoredMetric } from '../common-tile.model';

@Component({
  selector: 'bhub-hero-tile',
  templateUrl: './hero-tile.component.html',
  styleUrl: './hero-tile.component.scss',
  standalone: false
})
export class HeroTileComponent implements OnInit, ITileComponent {
  @Input() data: StoredTileDefinition;
  @Input() mode: UserBoardComponentMode;
  @Output() onIconClickEvent = new EventEmitter<string>();
  @Output() onPreviewItemClickEvent = new EventEmitter<string>();
  public model: StoredTileDefinition;
  availableIcons: string[] = ['info', 'notifications', 'star', 'check', 'warning', 'error', 'visibility'];

  public initialized = false;

  newIcon: string = '';
  selectedHeroMetric: StoredMetric;
  displayMetricName: string = '';

  //Make enum available for html
  UserBoardComponentMode = UserBoardComponentMode;

  //This needs to come from a service instead
  availableMetrics: StoredMetric[];

  filtersApplied: any = {};

  constructor(
    private calcEngineService: CalcEngineService,
    private metricService: MetricService,
    private selectedFilterUpdateObserver: SelectedFilterUpdatesService
  ) {
  }

  public validate(): string{
    // if(true == false){ //TODO: Add validation logic here in future
    //   return 'Please fix the mistake';
    // }
    // else {
      return '';
    // }
  }

  public ngOnInit() {

    //Set initial state of the filters as well as capture updates and make service calls when they change
    this.selectedFilterUpdateObserver.filters$.subscribe((filters) => {
      this.filtersApplied = filters;
      this.makeServiceRequest();
    });
  }

  makeServiceRequest(): void{
    this.initialized = false;

    if(this.mode === UserBoardComponentMode.Live){
      var request = this.generateTileRequest(this.data);
      this.calcEngineService.runCalcEngine(request, false).subscribe( (response) => {
        this.model = this.buildModelFromResponse(this.data, response);
        this.initialized = true;
      });
    }
    else if( this.mode === UserBoardComponentMode.Preview) {
      this.model = this.buildModelFromMockResponse(this.data);
      this.initialized = true;
    }
    else if( this.mode === UserBoardComponentMode.Edit) {
      if(this.data != null){
        this.buildInputModel(this.data);
        this.initialized = true;
      } else{
        this.buildInputModel();
        this.initialized = true;
      }
    }
  }


  generateTileRequest(data: StoredTileDefinition): CalcEngineRequest {
    const request = new CalcEngineRequest();

    // Prepare metricList based on data.heroRow (if it's a metric)
    if (data.heroRow.type === StoredHeroDataTypes.metric) {
      var hero = data.heroRow.value as StoredMetric;
      request.metricList.push({
        metricName: hero.metricName,
        metricId: hero.metricId
      });
    }


    data.footerRows.forEach(row => {
      row.forEach(piece => {
        if (piece.type === StoredHeroDataTypes.metric) {
          var metric = piece.value as StoredMetric;
          request.metricList.push({
            metricName: metric.metricName,
            metricId: metric.metricId
          });
        }
      });
    });

    request.filters = this.filtersApplied;

    return request;
  }



  // Build a new model from the service response and data
  buildModelFromResponse(data: StoredTileDefinition, response: CalcEngineResponse): StoredTileDefinition {
    // For hero tiles (no groupBy), there should be only one row with aggregated data
    const rowData = response.rowData.length > 0 ? response.rowData[0] : {};

    // Update hero row if it's a metric
    const updatedHeroRow = data.heroRow.type === StoredHeroDataTypes.metric && data.heroRow.value.metricId
      ? new HeroPiece(data.heroRow.type, rowData[data.heroRow.value.metricName]) // Use metricName to fetch value from response
      : new HeroPiece(data.heroRow.type,(data.heroRow.value));

    // Update footer rows by iterating over each HeroPiece
    const updatedFooterRows = data.footerRows.map(row =>
      row.map(piece => {
        if (piece.type === StoredHeroDataTypes.metric && piece.value.metricId) {
          // Match the metric name in the response
          return new HeroPiece(data.heroRow.type, rowData[piece.value.metricName]);
        }
        return new HeroPiece(data.heroRow.type, piece.value); // For text types, just return the value
      })
    );

    return {
      header: data.header,
      headerIcons: data.headerIcons || [],
      heroRow: updatedHeroRow,
      footerRows: updatedFooterRows
    };
  }


  // Build a new model from the service response and data
  buildModelFromMockResponse(data: StoredTileDefinition): StoredTileDefinition {
    // Update hero row if it's a metric
    const updatedHeroRow = data.heroRow.type === StoredHeroDataTypes.metric ? new HeroPiece(data.heroRow.type, "99.99") : new HeroPiece(data.heroRow.type, "XXXX");

    // Update footer rows by iterating over each HeroPiece
    const updatedFooterRows = data.footerRows.map(row =>
      row.map(piece => {
        if (piece.type === StoredHeroDataTypes.metric && piece.value.metricId) {
          // Match the metric name in the response
          return new HeroPiece(StoredHeroDataTypes.metric, "99.99");
        }
        return new HeroPiece(StoredHeroDataTypes.text, "XXXX"); // For text types, just return the value
      })
    );

    return {
      header: data.header,
      headerIcons: data.headerIcons || [],
      heroRow: updatedHeroRow,
      footerRows: updatedFooterRows
    };
  }

  // Build a new model from scratch
  buildInputModel(data: StoredTileDefinition = null): void {

    this.metricService.getMetricList().subscribe((metrics) => {

        //set convert service response to available metrics list
        this.availableMetrics = [];
        metrics.forEach((metric) => {
          this.availableMetrics.push(new StoredMetric(metric.metricId, metric.metricName));
        });

        if(data == null){
          const updatedHeroRow = new HeroPiece(StoredHeroDataTypes.metric, this.availableMetrics[0]);

          // Update footer rows by iterating over each HeroPiece
          const updatedFooterRows = [];
          updatedFooterRows.push([new HeroPiece(StoredHeroDataTypes.text, ""), new HeroPiece(StoredHeroDataTypes.text, "")]);

          this.model = new StoredTileDefinition(
            new HeroPiece(StoredHeroDataTypes.text, ""),
            [],
            updatedHeroRow,
            updatedFooterRows
          );
        }
        else {
          this.model = data;
        }

        /// TODO Consider adding logic to present text to user telling them to create metrics if none are available
    });
  }


  //Edit Mode methods:
  onMetricChange(event: any): void {
    // Find the selected metric based on the selected metricId
    this.selectedHeroMetric = this.availableMetrics.find(metric => metric.metricId === event.metricId);

    if (this.selectedHeroMetric) {
      // Set the model.heroRow.value to the selected metric object
      this.model.heroRow.value = this.selectedHeroMetric;
      // Also update displayMetricName to reflect the metricName
      this.displayMetricName = this.selectedHeroMetric.metricName;
    }
  }

  addHeaderIcon() {
    if (this.newIcon && !this.model.headerIcons.includes(this.newIcon)) {
      this.model.headerIcons.push(this.newIcon);
      this.newIcon = '';
    }
  }

  removeHeaderIcon(icon: string) {
    this.model.headerIcons = this.model.headerIcons.filter(i => i !== icon);
  }

  addFooterRow(): void {
    this.model.footerRows.push([
      new HeroPiece( StoredHeroDataTypes.text,  '' ),
      new HeroPiece( StoredHeroDataTypes.text,  '')
    ]);

  }

  removeFooterRow(index: number): void {
    this.model.footerRows.splice(index, 1);
  }


  onIconClick(iconText: string) {
    this.onIconClickEvent.emit( iconText );
  }

  onPreviewButtonClick(iconText: string) {
    this.onPreviewItemClickEvent.emit( iconText );
  }
}
